import * as z from "zod";
import { brandTypes } from "../catalog/enums";
import { anyOfFilters, code, codeName, cursorPagination, decimal, flag, pagination, uuidV7, version } from "../index";
import { syncStatuses } from "../til";
import {
  brandAssigneeRoles,
  creditInsurers,
  csScores,
  customerStages,
  duplicateMatches,
  legalForms,
  masterSources,
  orgKinds,
  orgTypes,
  salesTeamScores,
} from "./enums";
import { countryCode, currencyCode, emailAddress, organizationRef, shortText } from "./fields";

// The directory of the organizations (50-api/party.md §2.3, ADR-101): an organization with the business roles it has
// for Tomorrow (brand, supplier, customer), one aggregate with one version. Reference data travels by code.

const organizationFilters = {
  /** Part of the name, of the legal name, of the VAT number or of the customer or supplier code, case-insensitive. */
  q: z.string().trim().min(1).max(100).optional(),
  type: z.enum(orgTypes).optional(),
  kind: z.enum(orgKinds).optional(),
  /**
   * How a record of the organization stands on Business Central (I-26): its customer or its vendor, or with `kind`
   * the one of that role. `sync_failed` is the queue of who sends it again; `not_synced` with `kind: supplier`, the
   * suppliers still to register.
   */
  bcSyncStatus: z.enum(syncStatuses).optional(),
  /**
   * Only the customers without a contact in use with one of `orderContactRoles`: nobody of theirs hears of their
   * orders until Customer Service gives the role (ADR-158, ADR-180).
   */
  withoutOrderContact: flag.optional(),
  /**
   * Only the suppliers without their number on Business Central and with a purchase order neither closed nor
   * cancelled: none of those POs is confirmed until someone registers the supplier (ADR-093, ADR-189).
   */
  toRegisterOnBc: flag.optional(),
  /** Only the archived ones, duplicates merged into another included, instead of the others. */
  archived: flag.optional(),
};

/** The organizations in at least one of these sets of the filters (ADR-302): the queues of the directory, to do. */
const anyOfOrganizations = { anyOf: anyOfFilters(organizationFilters).optional() };

/** By name. Without `archived`, the ones that can be chosen: not archived and not merged into another. */
export const listOrganizationsInput = z.object({
  ...organizationFilters,
  ...anyOfOrganizations,
  ...pagination({ defaultSize: 25, maxSize: 100 }),
});
export type ListOrganizationsInput = z.input<typeof listOrganizationsInput>;

/** How many organizations the list has with the same filters, and since when the oldest waits (ADR-086). */
export const countOrganizationsInput = z.object({ ...organizationFilters, ...anyOfOrganizations });
export type CountOrganizationsInput = z.input<typeof countOrganizationsInput>;

export const getOrganizationInput = z.object({ organizationId: z.uuid() });
export type GetOrganizationInput = z.input<typeof getOrganizationInput>;

/** No id: the organization is always the caller's own (ADR-157). */
export const getOwnOrganizationInput = z.object({});
export type GetOwnOrganizationInput = z.input<typeof getOwnOrganizationInput>;

export const getOrganizationOptionsInput = z.object({});
export type GetOrganizationOptionsInput = z.input<typeof getOrganizationOptionsInput>;

/** Newest first: the organization, its business roles, its addresses and its contacts. */
export const getOrganizationHistoryInput = z.object({
  organizationId: z.uuid(),
  ...cursorPagination({ defaultLimit: 50, maxLimit: 100 }),
});
export type GetOrganizationHistoryInput = z.input<typeof getOrganizationHistoryInput>;
export const organizationHistoryQuery = getOrganizationHistoryInput.omit({ organizationId: true });

/** The legal data of an organization: `null` empties a field. */
export const organizationFields = z.object({
  name: shortText(200),
  legalName: shortText(200).nullable(),
  legalForm: z.enum(legalForms).nullable(),
  legalFormOther: shortText(100).nullable(),
  vatNumber: shortText(40).nullable(),
  companyRegNo: shortText(60).nullable(),
  incorporationYear: z.number().int().min(1700).max(2100).nullable(),
  /** Codice Destinatario of the Italian electronic invoice. */
  sdiCode: shortText(7).nullable(),
  pecEmail: emailAddress.nullable(),
  countryCode: countryCode.nullable(),
  websiteDomain: shortText(200).nullable(),
  email: emailAddress.nullable(),
  phone: shortText(40).nullable(),
});

/**
 * The id comes from the client (ADR-046): a retry by the same user returns the organization already created. Always
 * an external one: Tomorrow is the only internal organization.
 */
export const createOrganizationInput = organizationFields.partial().required({ name: true }).extend({ id: uuidV7 });
export type CreateOrganizationInput = z.input<typeof createOrganizationInput>;

/** PATCH: an absent field stays as it is. */
export const updateOrganizationInput = organizationFields.partial().extend({ organizationId: z.uuid(), version });
export type UpdateOrganizationInput = z.input<typeof updateOrganizationInput>;

/** An action on an organization, with the version the client read. */
export const organizationActionInput = z.object({ organizationId: z.uuid(), version });
export type OrganizationActionInput = z.input<typeof organizationActionInput>;

/** What makes an organization a brand. `brandType` is required the first time. */
export const brandFields = z.object({
  brandType: z.enum(brandTypes),
  /** The supplier that produces and invoices for the brand: an organization with the supplier role. */
  supplierOrgId: z.uuid().nullable(),
  excludeFromClientLinks: z.boolean(),
  /**
   * Where the customers answer its order confirmations, when not to Tomorrow (ACW on NetSuite, ADR-202): the email
   * leaves from Tomorrow with this address as its Reply-To.
   */
  orderConfirmationSender: emailAddress.nullable(),
});

/**
 * The commercial data of a brand (ADR-303): what it is to Tomorrow's sales and whether the client links offer it, the
 * fields HubSpot gives while it is the master. The CRM administrator changes these alone (`brand.commercial.manage`).
 */
export const brandCommercialFields = ["brandType", "excludeFromClientLinks"] as const satisfies readonly (keyof z.infer<
  typeof brandFields
>)[];

/** Gives the organization the brand role, or changes it (PATCH): the version is the one of the organization. */
export const saveBrandInput = brandFields.partial().extend({ organizationId: z.uuid(), version });
export type SaveBrandInput = z.input<typeof saveBrandInput>;

/**
 * The crew of Tomorrow on a brand, written whole (ADR-289): internal users by role, in the order given, which is
 * their position among the users with the same role. HubSpot gives it from its owners while it is the master.
 */
export const setBrandCrewInput = z.object({
  organizationId: z.uuid(),
  version,
  crew: z.array(z.object({ role: z.enum(brandAssigneeRoles), userId: z.uuid() })).max(50),
});
export type SetBrandCrewInput = z.input<typeof setBrandCrewInput>;

const supplierFields = z.object({
  supplierCode: shortText(40).nullable(),
  /** Code of the company of the group the supplier invoices. */
  legalEntity: code.nullable(),
  currency: currencyCode.nullable(),
  portalAccess: z.boolean(),
});

export const saveSupplierInput = supplierFields.partial().extend({ organizationId: z.uuid(), version });
export type SaveSupplierInput = z.input<typeof saveSupplierInput>;

/** An amount that is never negative. */
const amount = decimal.regex(/^\d/);

const customerFields = z.object({
  /** A prospect Sales follows, or a customer (ADR-289): from prospect to customer only. A new role is a customer. */
  stage: z.enum(customerStages),
  /** CUS code (Joor, legacy NetSuite); the customer number of Business Central is not here. */
  customerCode: shortText(20).nullable(),
  /** Code of the company of the group that invoices the customer. */
  legalEntity: code.nullable(),
  storeName: shortText(200).nullable(),
  currency: currencyCode.nullable(),
  defaultPriceList: code.nullable(),
  defaultPaymentTerms: code.nullable(),
  defaultDeliveryTerm: code.nullable(),
  /** An internal user: the area manager of every order of the customer, over the one of the ship-to country (ADR-246). */
  areaManagerUserId: z.uuid().nullable(),
  /** The Customer Service rep and the sales rep of the customer, internal users: in copy of its order confirmations (ADR-202). */
  csRepUserId: z.uuid().nullable(),
  salesRepUserId: z.uuid().nullable(),
  salesTeamScore: z.enum(salesTeamScores).nullable(),
  csScore: z.enum(csScores).nullable(),
  /** Forces the credit check to pass (allocation rule R1). */
  allowFulfillmentOverride: z.boolean(),
  /** Credit Control holds the credit of the customer: its orders are blocked (R1, ADR-179). */
  onHold: z.boolean(),
  isBlacklisted: z.boolean(),
  blacklistReason: shortText(2000).nullable(),
  creditInsurer: z.enum(creditInsurers),
  insurerAccountCode: shortText(10).nullable(),
  insuredAmount: amount.nullable(),
  /** 1-2 fine, 3 borderline, 4 no. */
  cribisRating: z.number().int().min(1).max(4).nullable(),
  isIndividual: z.boolean(),
  isEcommerce: z.boolean(),
  /** A customer of Farfetch: its orders stay out of the allocation (ADR-179). */
  isFarfetch: z.boolean(),
  /** Code of the company of the group the customer stands for: its orders are intercompany (ADR-179). */
  representingLegalEntity: code.nullable(),
});

/**
 * The commercial data of a customer (ADR-303): who follows it for Tomorrow and how Sales rates it. Sales and the CRM
 * administrator change these alone (`customer.commercial.manage`); the legal, fiscal and credit data stay with the
 * e-TAF and with who keeps the directory.
 */
export const customerCommercialFields = [
  "areaManagerUserId",
  "salesRepUserId",
  "salesTeamScore",
] as const satisfies readonly (keyof z.infer<typeof customerFields>)[];

export const saveCustomerInput = customerFields.partial().extend({ organizationId: z.uuid(), version });
export type SaveCustomerInput = z.input<typeof saveCustomerInput>;

/**
 * B-49 point 3, ADR-222: Credit Control puts a customer on the blacklist, or takes it off, always with the reason, which
 * the history of the customer keeps with who. Its orders are blocked for credit (ADR-179) and stay out of every run
 * of the allocation, whatever a decision on them says. 422 `organization_invalid` (`blacklisted` / `invalid_value`)
 * for a customer already so; `version` of the organization (409). `isBlacklisted` and `blacklistReason` of
 * `saveCustomer` stay for the clients of before.
 */
export const setCustomerBlacklistInput = z.object({
  organizationId: z.uuid(),
  version,
  blacklisted: z.boolean(),
  reason: z.string().trim().min(1).max(2000),
});
export type SetCustomerBlacklistInput = z.input<typeof setCustomerBlacklistInput>;

/** Groups of customers that look like the same company, the largest first. */
export const listDuplicateOrganizationsInput = z.object({ ...pagination({ defaultSize: 25, maxSize: 100 }) });
export type ListDuplicateOrganizationsInput = z.input<typeof listDuplicateOrganizationsInput>;

/** As many duplicates as one merge takes: the apps stop offering more at the same number. */
export const maxMergeDuplicates = 20;

/** The customer that stays, with its version, and the duplicates merged into it, in the order their data is taken. */
export const mergeOrganizationsInput = z.object({
  organizationId: z.uuid(),
  version,
  duplicateIds: z.array(z.uuid()).min(1).max(maxMergeDuplicates),
});
export type MergeOrganizationsInput = z.input<typeof mergeOrganizationsInput>;

/** The gateway routes split the input: the id in the path, the rest in the body. */
export const organizationParams = organizationActionInput.pick({ organizationId: true });
export const organizationVersionBody = organizationActionInput.pick({ version: true });
export const updateOrganizationBody = updateOrganizationInput.omit({ organizationId: true });
export const saveBrandBody = saveBrandInput.omit({ organizationId: true });
export const setBrandCrewBody = setBrandCrewInput.omit({ organizationId: true });
export const saveSupplierBody = saveSupplierInput.omit({ organizationId: true });
export const saveCustomerBody = saveCustomerInput.omit({ organizationId: true });
export const setCustomerBlacklistBody = setCustomerBlacklistInput.omit({ organizationId: true });
export const mergeOrganizationsBody = mergeOrganizationsInput.omit({ organizationId: true });

export const organizationSummaryDto = z
  .object({
    id: z.uuid(),
    name: z.string(),
    type: z.enum(orgTypes),
    kinds: z.array(z.enum(orgKinds)),
    countryCode: z.string().nullable().optional(),
    vatNumber: z.string().nullable().optional(),
    customerCode: z.string().nullable().optional(),
    supplierCode: z.string().nullable().optional(),
    /** How its customer and its vendor stand on Business Central (I-26): null for a role it does not have. */
    customerBcSyncStatus: z.enum(syncStatuses).nullable().optional(),
    supplierBcSyncStatus: z.enum(syncStatuses).nullable().optional(),
    archivedAt: z.iso.datetime().nullable(),
    /** The organization a duplicate was merged into (ADR-101), which archived it: what the list says of it. */
    mergedInto: organizationRef.nullable(),
  })
  .meta({ id: "OrganizationSummary" });

/** The organization of the signed-in user, what it is to Tomorrow included: the context of an app (ADR-157). */
export const ownOrganizationDto = z
  .object({
    id: z.uuid(),
    name: z.string(),
    kinds: z.array(z.enum(orgKinds)),
  })
  .meta({ id: "OwnOrganization" });

/** A user of Tomorrow in the crew of a brand, as HubSpot gives it by owner (ADR-109) or Backstage writes it (ADR-289). */
const brandAssigneeDto = z.object({
  role: z.enum(brandAssigneeRoles),
  /** The order among the users with the same role: there can be more than one in operations support. */
  position: z.number().int(),
  user: z.object({ id: z.uuid(), name: z.string() }),
});

export const brandRoleDto = z
  .object({
    brandType: z.enum(brandTypes),
    supplier: organizationRef.nullable().optional(),
    excludeFromClientLinks: z.boolean().optional(),
    orderConfirmationSender: z.string().nullable().optional(),
    /** The crew of Tomorrow on the brand, by role and position. */
    crew: z.array(brandAssigneeDto).optional(),
  })
  .meta({ id: "BrandRole" });

export const supplierRoleDto = z
  .object({
    supplierCode: z.string().nullable().optional(),
    legalEntity: codeName.nullable().optional(),
    currency: z.string().nullable().optional(),
    portalAccess: z.boolean().optional(),
    /** How the vendor stands on Business Central (I-26): `not_synced` until someone registers it there. */
    bcSyncStatus: z.enum(syncStatuses).optional(),
  })
  .meta({ id: "SupplierRole" });

/** A partner of a customer that is a partnership: what its approved e-TAF says. */
const customerPartnerDto = z.object({
  id: z.uuid(),
  name: z.string(),
  addressLine: z.string().nullable(),
  city: z.string().nullable(),
  postalCode: z.string().nullable(),
  stateProvince: z.string().nullable(),
  countryCode: z.string().nullable(),
});

export const customerRoleDto = z
  .object({
    /** A prospect Sales follows, or a customer (ADR-289). */
    stage: z.enum(customerStages).optional(),
    customerCode: z.string().nullable().optional(),
    legalEntity: codeName.nullable().optional(),
    storeName: z.string().nullable().optional(),
    currency: z.string().nullable().optional(),
    defaultPriceList: z.string().nullable().optional(),
    defaultPaymentTerms: codeName.nullable().optional(),
    defaultDeliveryTerm: codeName.nullable().optional(),
    areaManager: z.object({ id: z.uuid(), name: z.string() }).nullable().optional(),
    csRep: z.object({ id: z.uuid(), name: z.string() }).nullable().optional(),
    salesRep: z.object({ id: z.uuid(), name: z.string() }).nullable().optional(),
    salesTeamScore: z.enum(salesTeamScores).nullable().optional(),
    csScore: z.enum(csScores).nullable().optional(),
    allowFulfillmentOverride: z.boolean().optional(),
    onHold: z.boolean().optional(),
    isBlacklisted: z.boolean().optional(),
    blacklistReason: z.string().nullable().optional(),
    creditInsurer: z.enum(creditInsurers).optional(),
    insurerAccountCode: z.string().nullable().optional(),
    insuredAmount: decimal.nullable().optional(),
    cribisRating: z.number().int().nullable().optional(),
    isIndividual: z.boolean().optional(),
    isEcommerce: z.boolean().optional(),
    isFarfetch: z.boolean().optional(),
    representingLegalEntity: codeName.nullable().optional(),
    /** Written by the approval of the e-TAF, never from here, like the partners. */
    etafCompleted: z.boolean().optional(),
    partners: z.array(customerPartnerDto).optional(),
    /** How the customer stands on Business Central (I-26): `not_synced` until its e-TAF is approved. */
    bcSyncStatus: z.enum(syncStatuses).optional(),
  })
  .meta({ id: "CustomerRole" });

/**
 * A field of the organization, or of one of its roles, that a form shows and does not change: a system is its master
 * while it has the record (ADR-116). `part` is where the field is: the organization, or the role.
 */
export const organizationReadOnlyFieldDto = z.object({
  part: z.enum(["organization", ...orgKinds]),
  field: z.string(),
  source: z.enum(masterSources),
});

/** The aggregate: the organization and the business roles it has, `null` for the ones it does not. */
export const organizationDto = z
  .object({
    id: z.uuid(),
    type: z.enum(orgTypes),
    name: z.string(),
    legalName: z.string().nullable().optional(),
    legalForm: z.enum(legalForms).nullable().optional(),
    legalFormOther: z.string().nullable().optional(),
    vatNumber: z.string().nullable().optional(),
    companyRegNo: z.string().nullable().optional(),
    incorporationYear: z.number().int().nullable().optional(),
    sdiCode: z.string().nullable().optional(),
    pecEmail: z.string().nullable().optional(),
    countryCode: z.string().nullable().optional(),
    websiteDomain: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
    kinds: z.array(z.enum(orgKinds)),
    brand: brandRoleDto.nullable(),
    supplier: supplierRoleDto.nullable(),
    customer: customerRoleDto.nullable(),
    /** The organization a duplicate was merged into. */
    mergedInto: organizationRef.nullable(),
    /** The ids HubSpot has for it, while it has the record: as a brand, as a company. */
    hubspotIds: z.array(z.object({ kind: z.enum(["brand", "company"]), hubspotId: z.string() })),
    readOnlyFields: z.array(organizationReadOnlyFieldDto),
    archivedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    version: z.number().int(),
  })
  .meta({ id: "Organization" });

/** What the forms of the directory offer (ADR-048): the reference data an organization, an address or a role names. */
export const organizationOptionsDto = z.object({
  countries: z.array(codeName),
  currencies: z.array(codeName),
  legalEntities: z.array(codeName),
  priceLists: z.array(z.string()),
  paymentTerms: z.array(codeName),
  deliveryTerms: z.array(codeName),
  /** Not archived, the newest code first: the seasons of the addresses in copy of the order confirmations (ADR-202). */
  seasons: z.array(codeName),
});

export const duplicateGroupDto = z
  .object({
    match: z.enum(duplicateMatches),
    /** What the organizations of the group have in common, as it was compared. */
    value: z.string(),
    organizations: z.array(organizationSummaryDto),
  })
  .meta({ id: "DuplicateGroup" });
